import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Trash2, Send, Eye, CheckCircle, Clock, AlertTriangle,
  FileText, DollarSign, Calendar, Mail, Building2, Upload,
  X, Save, RefreshCw, Bell, Download, Settings, ImagePlus
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCIES = [
  { code: 'AUD', symbol: '$', name: 'Australian Dollar' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'NZD', symbol: '$', name: 'NZ Dollar' },
  { code: 'CAD', symbol: '$', name: 'Canadian Dollar' },
];

function CreateInvoiceTab() {
  const [invoices, setInvoices] = useState([]);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [filter, setFilter] = useState('all');

  // Form state
  const [form, setForm] = useState({
    customer: { name: '', email: '', address: '', phone: '' },
    line_items: [{ description: '', quantity: 1, unit_price: 0 }],
    due_days: 14,
    notes: '',
    include_gst: true,
    gst_rate: 10,
    currency: 'AUD',
    currency_symbol: '$'
  });

  // Business profile form
  const [profileForm, setProfileForm] = useState({
    business_name: '',
    address: '',
    phone: '',
    email: '',
    abn: '',
    bank_details: '',
    default_payment_terms: 14,
    default_gst_rate: 10,
    default_notes: 'Thank you for your business!'
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invoicesRes, profileRes] = await Promise.all([
        axios.get(`${API}/generated-invoices/`, { withCredentials: true }),
        axios.get(`${API}/generated-invoices/business-profile`, { withCredentials: true })
      ]);
      setInvoices(invoicesRes.data.invoices || []);
      setBusinessProfile(profileRes.data);
      setProfileForm(profileRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddLineItem = () => {
    setForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, unit_price: 0 }]
    }));
  };

  const handleRemoveLineItem = (index) => {
    if (form.line_items.length === 1) return;
    setForm(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const handleLineItemChange = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const calculateTotals = () => {
    const subtotal = form.line_items.reduce((sum, item) => 
      sum + (item.quantity * item.unit_price), 0
    );
    const gstAmount = form.include_gst ? subtotal * (form.gst_rate / 100) : 0;
    const total = subtotal + gstAmount;
    return { subtotal, gstAmount, total };
  };

  const handleCreateInvoice = async () => {
    if (!form.customer.name || !form.customer.email) {
      toast.error('Customer name and email are required');
      return;
    }
    if (form.line_items.some(item => !item.description || item.unit_price <= 0)) {
      toast.error('All line items need description and price');
      return;
    }

    try {
      await axios.post(`${API}/generated-invoices/`, form, { withCredentials: true });
      toast.success('Invoice created');
      setShowCreateForm(false);
      setForm({
        customer: { name: '', email: '', address: '', phone: '' },
        line_items: [{ description: '', quantity: 1, unit_price: 0 }],
        due_days: businessProfile?.default_payment_terms || 14,
        notes: businessProfile?.default_notes || '',
        include_gst: true,
        gst_rate: businessProfile?.default_gst_rate || 10,
        currency: 'AUD',
        currency_symbol: '$'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/generated-invoices/${invoiceId}/send`, {}, { withCredentials: true });
      toast.success('Invoice sent');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    }
  };

  const handleSendReminder = async (invoiceId) => {
    try {
      await axios.post(`${API}/generated-invoices/${invoiceId}/reminder`, {}, { withCredentials: true });
      toast.success('Reminder sent');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await axios.post(`${API}/generated-invoices/${invoiceId}/mark-paid`, {}, { withCredentials: true });
      toast.success('Invoice marked as paid & added to income');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await axios.delete(`${API}/generated-invoices/${invoiceId}`, { withCredentials: true });
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleSaveProfile = async () => {
    try {
      const params = new URLSearchParams(profileForm).toString();
      await axios.post(`${API}/generated-invoices/business-profile?${params}`, {}, { withCredentials: true });
      toast.success('Business profile saved');
      setShowProfileModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save profile');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/generated-invoices/business-profile/logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Logo uploaded');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { bg: 'bg-slate-100', text: 'text-slate-700', icon: FileText },
      sent: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Send },
      paid: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
      overdue: { bg: 'bg-rose-100', text: 'text-rose-700', icon: AlertTriangle },
    };
    const badge = badges[status] || badges.draft;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const filteredInvoices = invoices.filter(inv => 
    filter === 'all' || inv.status === filter
  );

  const { subtotal, gstAmount, total } = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Invoice Generator</h2>
          <p className="text-sm text-slate-500">Create and send professional invoices to your customers</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowProfileModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
            data-testid="business-profile-btn"
          >
            <Building2 className="w-4 h-4" />
            Business Profile
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium"
            data-testid="create-invoice-btn"
          >
            <Plus className="w-4 h-4" />
            Create Invoice
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
          <p className="text-sm text-slate-500 mb-1">Total Invoices</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">{invoices.length}</p>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl p-4">
          <p className="text-sm text-blue-600 mb-1">Sent</p>
          <p className="text-2xl font-bold text-blue-700">{invoices.filter(i => i.status === 'sent').length}</p>
        </div>
        <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-xl p-4">
          <p className="text-sm text-emerald-600 mb-1">Paid</p>
          <p className="text-2xl font-bold text-emerald-700">{invoices.filter(i => i.status === 'paid').length}</p>
        </div>
        <div className="bg-rose-50 dark:bg-rose-900/30 rounded-xl p-4">
          <p className="text-sm text-rose-600 mb-1">Overdue</p>
          <p className="text-2xl font-bold text-rose-700">{invoices.filter(i => i.status === 'overdue').length}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        {['all', 'draft', 'sent', 'paid', 'overdue'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              filter === f 
                ? 'bg-indigo-600 text-white' 
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Invoice List */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        {filteredInvoices.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No invoices yet</p>
            <p className="text-sm text-slate-400">Click &quot;Create Invoice&quot; to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {filteredInvoices.map((invoice) => (
              <div key={invoice.invoice_id} className="p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/40 rounded-full flex items-center justify-center">
                    <FileText className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900 dark:text-white">{invoice.invoice_number}</p>
                      {getStatusBadge(invoice.status)}
                    </div>
                    <p className="text-sm text-slate-500">{invoice.customer.name} • {invoice.customer.email}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Due: {invoice.due_date}
                      </span>
                      {invoice.reminders_sent > 0 && (
                        <span className="flex items-center gap-1">
                          <Bell className="w-3 h-3" />
                          {invoice.reminders_sent} reminder{invoice.reminders_sent > 1 ? 's' : ''} sent
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <p className="text-lg font-bold text-slate-900 dark:text-white">
                    {invoice.currency_symbol}{invoice.total?.toLocaleString()}
                  </p>
                  <div className="flex items-center gap-1">
                    {invoice.status === 'draft' && (
                      <button
                        onClick={() => handleSendInvoice(invoice.invoice_id)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Send Invoice"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    )}
                    {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                      <>
                        <button
                          onClick={() => handleSendReminder(invoice.invoice_id)}
                          className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
                          title="Send Reminder"
                        >
                          <Bell className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleMarkPaid(invoice.invoice_id)}
                          className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                          title="Mark as Paid"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDeleteInvoice(invoice.invoice_id)}
                      className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Invoice Modal */}
      <AnimatePresence>
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
            onClick={() => setShowCreateForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-3xl my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Create New Invoice</h3>
                <button onClick={() => setShowCreateForm(false)} className="p-2 text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                {/* Customer Details */}
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white mb-3">Customer Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Customer Name *"
                      value={form.customer.name}
                      onChange={(e) => setForm(prev => ({ ...prev, customer: { ...prev.customer, name: e.target.value } }))}
                      className="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    />
                    <input
                      type="email"
                      placeholder="Customer Email *"
                      value={form.customer.email}
                      onChange={(e) => setForm(prev => ({ ...prev, customer: { ...prev.customer, email: e.target.value } }))}
                      className="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    />
                    <input
                      type="text"
                      placeholder="Address (optional)"
                      value={form.customer.address}
                      onChange={(e) => setForm(prev => ({ ...prev, customer: { ...prev.customer, address: e.target.value } }))}
                      className="col-span-2 px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    />
                  </div>
                </div>

                {/* Line Items */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-slate-900 dark:text-white">Line Items</h4>
                    <button
                      onClick={handleAddLineItem}
                      className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Add Item
                    </button>
                  </div>
                  <div className="space-y-3">
                    {form.line_items.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Description"
                          value={item.description}
                          onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                          className="flex-1 px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                        />
                        <input
                          type="number"
                          placeholder="Qty"
                          value={item.quantity}
                          onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-20 px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                        />
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{form.currency_symbol}</span>
                          <input
                            type="number"
                            placeholder="Price"
                            value={item.unit_price}
                            onChange={(e) => handleLineItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            className="w-28 pl-7 pr-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                          />
                        </div>
                        <span className="w-24 text-right font-medium text-slate-900 dark:text-white">
                          {form.currency_symbol}{(item.quantity * item.unit_price).toFixed(2)}
                        </span>
                        {form.line_items.length > 1 && (
                          <button
                            onClick={() => handleRemoveLineItem(index)}
                            className="p-2 text-slate-400 hover:text-rose-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Payment Terms</label>
                    <select
                      value={form.due_days}
                      onChange={(e) => setForm(prev => ({ ...prev, due_days: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    >
                      <option value={7}>Due in 7 days</option>
                      <option value={14}>Due in 14 days</option>
                      <option value={30}>Due in 30 days</option>
                      <option value={45}>Due in 45 days</option>
                      <option value={60}>Due in 60 days</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Currency</label>
                    <select
                      value={form.currency}
                      onChange={(e) => {
                        const curr = CURRENCIES.find(c => c.code === e.target.value);
                        setForm(prev => ({ ...prev, currency: curr.code, currency_symbol: curr.symbol }));
                      }}
                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    >
                      {CURRENCIES.map(c => (
                        <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* GST Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">Include GST</p>
                    <p className="text-sm text-slate-500">Add GST to invoice total</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {form.include_gst && (
                      <input
                        type="number"
                        value={form.gst_rate}
                        onChange={(e) => setForm(prev => ({ ...prev, gst_rate: parseFloat(e.target.value) || 0 }))}
                        className="w-16 px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-center"
                      />
                    )}
                    <span className="text-sm text-slate-500">%</span>
                    <button
                      onClick={() => setForm(prev => ({ ...prev, include_gst: !prev.include_gst }))}
                      className={`relative w-12 h-6 rounded-full transition-colors ${form.include_gst ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-slate-600'}`}
                    >
                      <span className={`absolute top-1 left-0 w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-200 ${form.include_gst ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Notes</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Payment terms, bank details, thank you message..."
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white h-20 resize-none"
                  />
                </div>

                {/* Totals */}
                <div className="bg-indigo-50 dark:bg-indigo-900/30 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between text-slate-600 dark:text-slate-400">
                    <span>Subtotal</span>
                    <span>{form.currency_symbol}{subtotal.toFixed(2)}</span>
                  </div>
                  {form.include_gst && (
                    <div className="flex justify-between text-slate-600 dark:text-slate-400">
                      <span>GST ({form.gst_rate}%)</span>
                      <span>{form.currency_symbol}{gstAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xl font-bold text-indigo-700 dark:text-indigo-300 pt-2 border-t border-indigo-200 dark:border-indigo-800">
                    <span>Total</span>
                    <span>{form.currency_symbol}{total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateInvoice}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Create Invoice
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Business Profile Modal */}
      <AnimatePresence>
        {showProfileModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowProfileModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Business Profile</h3>
                <button onClick={() => setShowProfileModal(false)} className="p-2 text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Logo */}
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center overflow-hidden">
                    {businessProfile?.logo ? (
                      <img src={businessProfile.logo} alt="Logo" className="w-full h-full object-contain" />
                    ) : (
                      <ImagePlus className="w-8 h-8 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <label className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium cursor-pointer">
                      Upload Logo
                      <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                    </label>
                    <p className="text-xs text-slate-500 mt-1">PNG, JPG up to 2MB</p>
                  </div>
                </div>

                <input
                  type="text"
                  placeholder="Business Name"
                  value={profileForm.business_name}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, business_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                />
                <textarea
                  placeholder="Business Address"
                  value={profileForm.address}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white h-20 resize-none"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Phone"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                    className="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  />
                </div>
                <input
                  type="text"
                  placeholder="ABN / Tax ID"
                  value={profileForm.abn}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, abn: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                />
                <textarea
                  placeholder="Bank Details (BSB, Account Number, etc.)"
                  value={profileForm.bank_details}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, bank_details: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white h-20 resize-none"
                />
                <textarea
                  placeholder="Default Notes / Terms"
                  value={profileForm.default_notes}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, default_notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white h-16 resize-none"
                />
              </div>

              <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
                <button
                  onClick={() => setShowProfileModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium"
                >
                  Save Profile
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default CreateInvoiceTab;
